import { ViewContainerRef } from "@angular/core";
import { IContentControlManager } from 'app/js/src/domain/content-control/manager';
import { IEvrideoDialogService } from "app/js/src/domain/common/i-evrideo-dialog-service";
import { IContentControlStudioTimeline } from "../content-control-studio-timeline/i-content-control-studio-timeline";
import {
  ContentControlledEditingDirectiveViewModel,
} from 'app/js/src/domain/content-control/editing-directive';
import {
  ContentControlledEventActionEnum,
  ContentControlledMediaTypeEnum,
} from 'app/js/src/domain/content-control/enums';
import { ContentControlCanvasService } from 'app/js/src/infrastructure/content-control/content-control-canvas.service';
import { CanvasBaseObject } from 'app/js/src/domain/content-control/canvas';
import { IStudioEventsRef } from 'app/js/src/domain/content-control/studio';
import { ETimelineMode } from 'app/js/src/domain/content-control/studio/e-timeline-mode';
import { IStudioViewModelContext } from "./i-studio-view-model-context";
import { StudioTimelineZoomManager } from "./studio-timeline-zoom-manager";
import { EffectAreaInteractionHandler } from "./effect-area-interaction-handler";
import { StudioEventCoordinator } from "./studio-event-coordinator";

/**
 * Manages segment selection, creation, and editing within the content control studio.
 * Handles mark-in/mark-out operations, multi-select, segment-to-event conversion,
 * and coordinates with the timeline, canvas, and zoom systems when segments change.
 */
export class StudioSegmentController {
  /** Coordinator responsible for converting segments to events and opening subtitle dialogs. */
  private _events: StudioEventCoordinator;

  /** All editing directive segments currently displayed on the timeline. */
  public segments: ContentControlledEditingDirectiveViewModel[] = [];
  /** IDs of segments selected in a multi-select operation. Empty when single-select is active. */
  public multiEventsSelectedIds: number[] = [];

  constructor(
    private readonly _manager: IContentControlManager,
    private readonly _canvasService: ContentControlCanvasService,
    private readonly _timeline: IContentControlStudioTimeline,
    private readonly _dialogService: IEvrideoDialogService,
    private readonly _viewContainerRef: ViewContainerRef,
    private readonly _context: IStudioViewModelContext,
    private readonly _zoom: StudioTimelineZoomManager,
    private readonly _effectArea: EffectAreaInteractionHandler,
    private readonly _eventsRef: IStudioEventsRef,
  ) {}

  /**
   * Injects the event coordinator after construction to break the circular dependency
   * between this controller and the event coordinator.
   */
  public setEventCoordinator(events: StudioEventCoordinator): void {
    this._events = events;
  }

  /**
   * Selects a segment by ID, or deselects all if `id` is negative.
   *
   * On deselection (id < 0): removes unsaved segments (mediaType === None), clears selection
   * and multi-select state, and hides the effect area overlay.
   *
   * On selection: validates that Cut events cannot be selected while in "Editing Time" mode,
   * opens the subtitle editor if the segment is a subtitle event that is already selected,
   * toggles the effect area SVG for incomplete Video events, and adjusts the timeline
   * zoom/position to keep the selected segment visible.
   *
   * @param id - Segment ID to select, or a negative value to deselect.
   * @param isMulti - When `true`, preserves the existing multi-select list instead of clearing it.
   */
  public changeSelectedSegment(id: number, isMulti: boolean = false): void {
    const manager = this._manager;
    const timeline = this._timeline;
    if (id < 0) {
      if (manager.selectedSegment?.mediaType === ContentControlledMediaTypeEnum.None) {
        const index = this.segments.findIndex((x) => x.id === manager.selectedSegment.id);
        if (index > -1) this.segments.splice(index, 1);
      }
      manager.selectedSegment = null;
      this._canvasService.drawEffectAreaSVG = false;
      this.multiEventsSelectedIds = [];
    } else {
      const segment = this.segments.find((x) => x.id === id);
      if (segment) {
        if (
          segment.action === ContentControlledEventActionEnum.Cut &&
          this._context.timelineMode === ETimelineMode.EditedTime
        ) {
          this._dialogService.openDialog(this._viewContainerRef, {
            type: "info",
            title: "Action not available",
            content: 'Editing Cut events while timeline is in "Editing time" is not available',
            timeout: 5000,
          });
          return;
        }
        if (manager.selectedEvent?.id === id && segment.IsEditSubtitleEvent) {
          this._events?.openSubtitleEditorDialog(segment);
          return;
        }
        if (!isMulti) this.multiEventsSelectedIds = [];
        manager.selectedSegment = segment;
        this._context.markForCheck();
      }
      if (manager.selectedSegmentIsEvent) {
        if (manager.selectedEvent.mediaType === ContentControlledMediaTypeEnum.Video) {
          if (segment.isConfigurationComplete === false) {
            this._canvasService.resetDrawEffectArea(
              this._effectArea.getCanvasObjectFromEvent(manager.selectedEvent) as CanvasBaseObject,
            );
            this._canvasService.drawEffectAreaSVG = true;
          }
        } else {
          this._canvasService.drawEffectAreaSVG = false;
        }
        if (segment.IsEditSubtitleEvent) {
          this._events?.openSubtitleEditorDialog(segment);
        }
      }
      setTimeout(() => {
        if (this._zoom.isFitSegment) {
          this._zoom.timelineZoomFitSegment();
        } else {
          timeline.translateTimeline();
          timeline.goToSelectedEventTime("in");
        }
      }, 0);
    }
    setTimeout(() => timeline.rebuildSegmentsInternally(this.segments), 0);
  }

  /**
   * Deletes the keyframe at the current playback position for the selected event,
   * if the event has an incomplete configuration and the current time falls on a
   * keyframe tween. Resets the effect area canvas after removal.
   */
  public chooseWhatToDelete(): void {
    const manager = this._manager;
    const videoSource = this._context.getVideoSource();
    if (
      manager.selectedEvent &&
      manager.selectedSegment &&
      manager.selectedSegment.isConfigurationComplete === false &&
      manager.selectedEvent.isKeyframeTween(videoSource.currentPlayTimeAsClosestFrameMs())
    ) {
      const currentKeyframe = manager.selectedEvent.getKeyframeByTime(
        videoSource.currentPlayTimeAsClosestFrameMs(),
      );
      this._canvasService.resetDrawEffectArea(currentKeyframe);
      manager.removeSelectedEventKeyframe(currentKeyframe);
    }
  }

  /**
   * Sets the in-point (start time) of the active segment to the current playback position.
   *
   * If the segment has no end time yet, calculates one from the context. If the new start
   * exceeds the current end, shifts the end forward by the segment's duration.
   *
   * Overlapping Cut segments are accounted for: the start is pushed past any cut that covers
   * the in-point, and the end is clamped before any subsequent cut.
   *
   * For event-backed segments, delegates to {@link StudioEventCoordinator.segmentToEvent}
   * for server-side persistence. If that conversion fails, the original start/end are restored.
   */
  public async markIn(): Promise<void> {
    const manager = this._manager;
    const timeline = this._timeline;
    const videoSource = this._context.getVideoSource();
    const useTimestamp = this._context.shouldUseTimestampForMarkIn();
    const segment = this.getActiveSegment();
    segment.createdAt = Date.now();
    const activeSegmentStartsAt = segment.startsAtMs;
    const activeSegmentEndsAt = segment.endsAtMs;

    let startsAt: number;
    if (useTimestamp) {
      const frameAlignedPlayerMs = videoSource.currentPlayTimeAsClosestFrameMs(videoSource.rawCurrentTimeSecs);
      startsAt = this._context.convertPlayerTimeToTimestampMs(frameAlignedPlayerMs / 1000);
    } else {
      startsAt = videoSource.currentPlayTimeAsClosestFrameMs();
    }
    let endsAt = null;
    if (segment.endsAtMs === 0) {
      const tSecs = this._context.getEndTimeForMarkIn(timeline.currentVideoTimeInMs());
      endsAt = videoSource.currentPlayTimeAsClosestFrameMs(tSecs);
      if (useTimestamp) {
        endsAt = this._context.convertPlayerTimeToTimestampMs(endsAt / 1000);
      }
    } else if (startsAt >= segment.endsAtMs) {
      endsAt = videoSource.currentPlayTimeAsClosestFrameMs((startsAt + segment.durationMs) / 1000);
      if (useTimestamp) {
        endsAt = this._context.convertPlayerTimeToTimestampMs(endsAt / 1000);
      }
      if (endsAt > timeline.currentVideoTimeInMs()) endsAt = timeline.currentVideoTimeInMs();
    }
    segment.startsAtMs = startsAt;
    segment.endsAtMs = endsAt || segment.endsAtMs;

    let overlappingCutEvents = timeline.getOverlappingCutSegmentsInDuration(
      segment.startsAtMs, segment.endsAtMs, segment.id,
    );
    if (overlappingCutEvents && overlappingCutEvents.length > 0) {
      const oneFrameInMs = timeline.oneFrameSecs * 1000;
      const startCutIndex = overlappingCutEvents.findLastIndex(
        (x) => x.startsAtMs <= segment.startsAtMs && x.endsAtMs >= segment.startsAtMs,
      );
      const newStartAt =
        startCutIndex > -1
          ? overlappingCutEvents[startCutIndex].endsAtMs + oneFrameInMs
          : segment.startsAtMs;
      if (newStartAt >= segment.endsAtMs) {
        segment.endsAtMs = videoSource.currentPlayTimeAsClosestFrameMs((newStartAt + segment.durationMs) / 1000);
        if (useTimestamp) {
          segment.endsAtMs = this._context.convertPlayerTimeToTimestampMs(segment.endsAtMs / 1000);
        }
      }
      segment.startsAtMs = newStartAt;
      overlappingCutEvents = timeline.getOverlappingCutSegmentsInDuration(
        segment.startsAtMs, segment.endsAtMs, segment.id,
      );
      if (overlappingCutEvents.length > 0) {
        segment.endsAtMs = overlappingCutEvents[0].startsAtMs - oneFrameInMs;
      }
    }

    let postEventUpdate = true;
    if (manager.selectedSegmentIsEvent) {
      segment.createdAt = Date.now();
      postEventUpdate = (await this._events?.segmentToEvent(manager.selectedSegment)) ?? true;
    }
    if (postEventUpdate) {
      this._updateLocalSegment(segment);
      manager.updateSelectedSegment(segment, true);
      setTimeout(() => {
        if (this._zoom.isFitSegment) {
          this._zoom.timelineZoomFitSegment();
        } else {
          timeline.rebuildSegmentsInternally(this.segments);
        }
      }, 0);
    } else {
      segment.startsAtMs = activeSegmentStartsAt;
      segment.endsAtMs = activeSegmentEndsAt;
    }
  }

  /**
   * Sets the out-point (end time) of the active segment to the current playback position.
   *
   * Returns early if the new end time would be at or before the segment's start.
   * Clamps the end time before any overlapping Cut segment that starts within the range.
   *
   * For event-backed segments, delegates to {@link StudioEventCoordinator.segmentToEvent}
   * for server-side persistence. If that conversion fails, the original end time is restored.
   */
  public async markOut(): Promise<void> {
    const manager = this._manager;
    const timeline = this._timeline;
    const videoSource = this._context.getVideoSource();
    const segment = this.getActiveSegment();
    const activeSegmentEndsAt = segment.endsAtMs;
    const useTimestampForEnd = this._context.shouldUseTimestampForMarkIn();
    let endsAt: number;
    if (useTimestampForEnd) {
      const frameAlignedPlayerMs = videoSource.currentPlayTimeAsClosestFrameMs(videoSource.rawCurrentTimeSecs);
      endsAt = this._context.convertPlayerTimeToTimestampMs(frameAlignedPlayerMs / 1000);
    } else {
      endsAt = videoSource.currentPlayTimeAsClosestFrameMs();
    }
    if (segment.startsAtMs >= endsAt) return;
    segment.endsAtMs = endsAt;

    let overlappingCutEvents = timeline.getOverlappingCutSegmentsInDuration(
      segment.startsAtMs, segment.endsAtMs, segment.id,
    );
    if (overlappingCutEvents && overlappingCutEvents.length > 0) {
      const oneFrameInMs = timeline.oneFrameSecs * 1000;
      const startCutIndex = overlappingCutEvents.findIndex(
        (x) => x.startsAtMs <= segment.endsAtMs,
      );
      segment.endsAtMs =
        startCutIndex > -1
          ? overlappingCutEvents[startCutIndex].startsAtMs - oneFrameInMs
          : segment.endsAtMs;
    }

    let postEventUpdate = true;
    if (manager.selectedSegmentIsEvent) {
      postEventUpdate = (await this._events?.segmentToEvent(manager.selectedSegment)) ?? true;
    }
    if (postEventUpdate) {
      this._updateLocalSegment(segment);
      manager.updateSelectedSegment(segment, true);
      setTimeout(() => {
        if (this._zoom.isFitSegment) {
          this._zoom.timelineZoomFitSegment("out");
        } else {
          timeline.rebuildSegmentsInternally(this.segments);
        }
      }, 0);
    } else {
      segment.endsAtMs = activeSegmentEndsAt;
    }
  }

  /**
   * Returns the currently selected segment, or creates and registers a new empty segment
   * if none is selected. Used by mark-in/mark-out to ensure there is always a segment to edit.
   */
  public getActiveSegment(): ContentControlledEditingDirectiveViewModel {
    const manager = this._manager;
    let segment = manager.selectedSegment;
    if (!segment) {
      segment = manager.createNewSegment();
      this.segments.push(segment);
    }
    return segment;
  }

  /**
   * Rebuilds the local segments list from the manager's editing directives.
   * Preserves the currently selected segment even if it is not yet persisted
   * (i.e., not part of the directives). Triggers a timeline rebuild and
   * re-initialises event bindings via {@link IStudioEventsRef.setupEvents}.
   */
  public eventsChangeDetected(): void {
    const manager = this._manager;
    const newSegments = manager.editingDirectives.map((x) => x.toView(0));
    if (
      manager.selectedSegment &&
      !newSegments.map((x) => x.id).includes(manager.selectedSegment.id)
    ) {
      newSegments.push(manager.selectedSegment);
    }
    this.segments = [...newSegments];
    this._timeline.rebuildSegmentsInternally(this.segments);
    this._eventsRef?.setupEvents();
  }

  /**
   * Reverts or removes an editing directive event by ID.
   * If the event exists in the reportable events list, it is reverted to its original state;
   * otherwise it is removed entirely (i.e., it was newly created and never saved).
   */
  public onRevertEventClicked(id: number): void {
    const manager = this._manager;
    const event = manager.editingDirectives.find((x) => x.id === id);
    if (!event) return;
    const index = manager.reportableEvents.findIndex((x) => x.id === id);
    if (index > -1) {
      manager.revertEditingDirectiveEvent(event);
    } else {
      manager.removeEditingDirectiveEvent(event);
    }
  }

  /**
   * Handles a multi-select action from the timeline. Stores the selected IDs and
   * selects the first segment (or deselects all if the list is empty).
   * @param ids - IDs of the segments included in the multi-selection.
   */
  public onMultiEventsClicked(ids: number[]): void {
    this.multiEventsSelectedIds = ids;
    this.changeSelectedSegment(ids.length > 0 ? ids[0] : -1, true);
  }

  /**
   * Called when the user drags or resizes a segment directly on the timeline.
   * Updates the manager's selected segment with the new timing, converts it
   * to an event if applicable via the provided callback, and rebuilds the timeline.
   * @param timelineSegment - The segment view model with updated start/end times. No-op if `null`.
   * @param segmentToEventFn - Callback that persists the segment as an event on the server.
   */
  public async activeSegmentChangedOnTimeline(
    timelineSegment: ContentControlledEditingDirectiveViewModel,
    segmentToEventFn: (seg: ContentControlledEditingDirectiveViewModel) => Promise<void>,
  ): Promise<void> {
    if (timelineSegment === null) return;
    const manager = this._manager;
    manager.updateSelectedSegment(timelineSegment, true);
    if (manager.selectedSegmentIsEvent) {
      await segmentToEventFn(manager.selectedSegment);
    }
    this._timeline.rebuildSegmentsInternally(this.segments);
  }

  /**
   * Synchronises the start/end times of a segment in the local {@link segments} array
   * with the values from the given segment instance (which may have been mutated by mark-in/out).
   */
  private _updateLocalSegment(segment: ContentControlledEditingDirectiveViewModel): void {
    const localSegment = this.segments.find((x) => x.id === segment.id);
    if (localSegment) {
      localSegment.startsAtMs = segment.startsAtMs;
      localSegment.endsAtMs = segment.endsAtMs;
    }
  }
}
